<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ConfigurationPage from './ConfigurationPage.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const checks = computed(() => [
  {
    key: 'site',
    complete: Boolean(props.inbox.website_url),
    title: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.site.TITLE'),
    completeText: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.site.COMPLETE'),
    incompleteText: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.site.INCOMPLETE'),
  },
  {
    key: 'domains',
    complete: Boolean(props.inbox.allowed_domains?.trim()),
    title: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.domains.TITLE'),
    completeText: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.domains.COMPLETE'),
    incompleteText: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.domains.INCOMPLETE'),
  },
  {
    key: 'identity',
    complete: Boolean(props.inbox.hmac_mandatory),
    title: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.identity.TITLE'),
    completeText: t('INBOX_MGMT.MESSENGER_INSTALL.CHECKS.identity.COMPLETE'),
    incompleteText: t(
      'INBOX_MGMT.MESSENGER_INSTALL.CHECKS.identity.INCOMPLETE'
    ),
  },
]);

const completedChecks = computed(
  () => checks.value.filter(check => check.complete).length
);
const isReady = computed(() => completedChecks.value === checks.value.length);
</script>

<template>
  <div class="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(19rem,0.65fr)]">
    <section
      class="overflow-hidden rounded-lg border border-n-weak bg-n-solid-1"
    >
      <header
        class="flex flex-wrap items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
      >
        <div>
          <h2 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.MESSENGER_INSTALL.INSTALL_TITLE') }}
          </h2>
          <p class="mb-0 text-sm text-n-slate-10">
            {{ $t('INBOX_MGMT.MESSENGER_INSTALL.INSTALL_DESCRIPTION') }}
          </p>
        </div>
        <span
          class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium outline outline-1 -outline-offset-1"
          :class="
            isReady
              ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
              : 'bg-n-amber-3 text-n-amber-11 outline-n-amber-5'
          "
        >
          <span
            class="size-3.5"
            :class="isReady ? 'i-lucide-circle-check' : 'i-lucide-circle-alert'"
          />
          {{
            isReady
              ? $t('INBOX_MGMT.MESSENGER_INSTALL.READY')
              : $t('INBOX_MGMT.MESSENGER_INSTALL.ACTION_NEEDED')
          }}
        </span>
      </header>

      <div class="px-5 py-5">
        <div class="mb-4 flex items-center justify-between gap-3">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.MESSENGER_INSTALL.CODE_TITLE') }}
          </p>
          <a
            v-if="inbox.website_url"
            :href="inbox.website_url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
          >
            {{ $t('INBOX_MGMT.MESSENGER_INSTALL.OPEN_SITE') }}
            <span class="i-lucide-external-link size-3.5" />
          </a>
        </div>
        <woot-code :script="inbox.web_widget_script" lang="html" />
        <p class="mb-0 mt-3 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.MESSENGER_INSTALL.CODE_HELP') }}
        </p>
      </div>
    </section>

    <aside class="rounded-lg border border-n-weak bg-n-solid-1 px-5 py-4">
      <div class="mb-4 flex items-center justify-between gap-3">
        <h2 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.MESSENGER_INSTALL.READINESS_TITLE') }}
        </h2>
        <span class="text-xs font-medium text-n-slate-10">
          {{
            $t('INBOX_MGMT.MESSENGER_INSTALL.PROGRESS', {
              complete: completedChecks,
              total: checks.length,
            })
          }}
        </span>
      </div>
      <div class="divide-y divide-n-weak">
        <div
          v-for="check in checks"
          :key="check.key"
          class="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
        >
          <span
            class="mt-0.5 size-4 shrink-0"
            :class="
              check.complete
                ? 'i-lucide-circle-check text-n-teal-10'
                : 'i-lucide-circle-dashed text-n-slate-9'
            "
          />
          <div>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ check.title }}
            </p>
            <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
              {{ check.complete ? check.completeText : check.incompleteText }}
            </p>
          </div>
        </div>
      </div>
    </aside>

    <section class="xl:col-span-2">
      <div class="mb-4">
        <h2 class="mb-1 text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.MESSENGER_INSTALL.SECURITY_TITLE') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-10">
          {{ $t('INBOX_MGMT.MESSENGER_INSTALL.SECURITY_DESCRIPTION') }}
        </p>
      </div>
      <ConfigurationPage :inbox="inbox" />
    </section>
  </div>
</template>
